// Single source of truth for the mobile navigation wipe's timing and geometry.
//
// These values are genuinely coupled across three files — the overlay markup,
// the nav store's close bookkeeping, and the header logo's fade-back-in delay —
// so they live here rather than being restated as literals in each place. An
// earlier version documented the coupling in prose while every consumer carried
// its own `660`, which meant retuning one silently desynced the others.

export const BAR_COUNT = 8;
export const BAR_STAGGER = 50;
export const BAR_DURATION = 500;

// The overlay lingers this long before retreating, so it acts as a curtain over
// an Astro view transition. The desktop SVG decoration uses the same value.
export const CLOSE_DELAY = 660;

// The last bar to start moving on close. Closing runs the stagger backwards, so
// which bar covers any given point depends on viewport aspect ratio — waiting
// for the final bar to start is the only bound that holds at every size.
export const LAST_BAR_START = CLOSE_DELAY + (BAR_COUNT - 1) * BAR_STAGGER;

// Everything is off screen once that last bar finishes travelling.
export const CLOSE_TOTAL = LAST_BAR_START + BAR_DURATION;

// The header logo fades back in on mobile once the wipe can no longer be
// covering it. The logo sits top-left, which the -45deg rotation maps to a
// middle bar index (~2.5 to ~5.5 across plausible aspect ratios), so it is
// neither first nor last out — deriving from LAST_BAR_START is what makes this
// safe at every viewport rather than at the one that happened to be tested.
export const LOGO_RETURN_DELAY = LAST_BAR_START;

// Rotating the bar grid -45deg leaves the viewport corners bare, and angling the
// bar ends (one bar width of slant) pulls the tips further in still. Coverage
// needs:
//   margin per side = (S - (100vw + 100vh) / sqrt(2)) / 2   >=   S / BAR_COUNT
//   => S >= (1 / sqrt(2)) * (100vw + 100vh) / (1 - 2 / BAR_COUNT)
// Derived rather than hardcoded so changing BAR_COUNT can't silently uncover the
// corners. The 1.03 is margin against sub-pixel rounding.
export const GRID_FACTOR = Number((Math.SQRT1_2 / (1 - 2 / BAR_COUNT) * 1.03).toFixed(3));

// Fraction of a bar's length that is actually on screen. The viewport spans
// +/-(100vw + 100vh)/(2 * sqrt(2)) along both grid axes, independent of aspect
// ratio, while the grid spans +/-S/2.
export const VISIBLE_FRACTION = Math.SQRT1_2 / GRID_FACTOR;

// Where to place the gradient stops so the ramp spans the visible band rather
// than the oversized grid. Painting `to-b` across the whole bar would show only
// the middle of the ramp, washing out both ends.
export const RAMP_INSET = Number((((1 - VISIBLE_FRACTION) / 2) * 100).toFixed(1));
